import {
  MAX_IMPORTANT_METADATA_COUNT,
  STAR_METADATA_MAX_LIMIT_MESSAGE,
} from '@modules/integrationsV2/constants';
import { useFetchMetadataDetailsForEqlSource } from '@modules/integrationsV2/hooks';
import { EditMetadataInput } from '@modules/integrationsV2/types';
import { getDisplayNameForMetadataType } from '@modules/integrationsV2/utils';
import { MetadataDetailsViewFragment } from 'generated/graphql';
import { useMemo } from 'react';

interface ValidateEditMetadataInputOptions {
  eqlSource: string;
  input: EditMetadataInput;
  metadataToEdit: MetadataDetailsViewFragment;
}

export const useValidateEditMetadataInput = ({
  input,
  eqlSource,
  metadataToEdit,
}: ValidateEditMetadataInputOptions) => {
  const { metadataDetails } = useFetchMetadataDetailsForEqlSource({
    eqlSource,
    fetchPolicy: 'cache-first', // We can use cache-first here, because we are fetching with cache-and-network in the root components
  });

  const errorTextForDisplayText = useMemo(() => {
    const isSameNameInSource = !!metadataDetails.find(
      (metadata) =>
        metadata.source === input.source &&
        metadata.key !== input.key &&
        metadata.namespace === input.namespace &&
        metadata.displayText === input.displayText,
    );

    if (isSameNameInSource) {
      return 'This cannot be saved as the metadata name as another metadata in the same source has this name';
    }

    const sameNameWithDifferentType = metadataDetails?.find(
      (metadata) =>
        metadata.displayText === input.displayText &&
        metadata.type !== metadataToEdit.type &&
        metadata.key !== input.key,
    );

    if (sameNameWithDifferentType) {
      return `You cannot change the metadata name to "${
        input.displayText
      }" as it is being used in the source "${
        sameNameWithDifferentType.source
      }" as a "${getDisplayNameForMetadataType(
        sameNameWithDifferentType.type,
      )}" data type`;
    }

    if (!input.displayText?.trim()) {
      return 'Metadata name cannot be empty';
    }

    return undefined;
  }, [
    input.displayText,
    input.key,
    input.namespace,
    input.source,
    metadataDetails,
    metadataToEdit.type,
  ]);

  const errorTextForIsImportant = useMemo(() => {
    const numberOfImportantMetadata =
      input.isImportant && !metadataToEdit.isImportant
        ? metadataDetails?.filter(
            (metadata) =>
              metadata.source === input.source &&
              metadata.isImportant &&
              metadata.namespace === input.namespace,
          ).length ?? 0
        : 0;

    if (numberOfImportantMetadata >= MAX_IMPORTANT_METADATA_COUNT) {
      return STAR_METADATA_MAX_LIMIT_MESSAGE;
    }

    return undefined;
  }, [
    input.isImportant,
    input.namespace,
    input.source,
    metadataDetails,
    metadataToEdit.isImportant,
  ]);

  const errorTextForDescription = useMemo(() => {
    if (!input.description?.trim()) {
      return 'Metadata description cannot be empty';
    }

    return undefined;
  }, [input.description]);

  const errorFor = {
    displayText: errorTextForDisplayText,
    isImportant: errorTextForIsImportant,
    description: errorTextForDescription,
  };

  return {
    hasError: Object.values(errorFor).some(Boolean),
    errorFor,
  };
};
